<script lang="ts">
  import { Icon, type IconName } from '$ui/primitives/Icon';
  import { Spinner } from '$ui/primitives/Spinner';
  import { toast, type ToastVariant } from './store.svelte';

  function iconFor(variant: ToastVariant): IconName {
    switch (variant) {
      case 'success': return 'check-circle';
      case 'error': return 'alert-circle';
      case 'info': return 'info';
      default: return 'info';
    }
  }
</script>

<div class="container" role="region" aria-label="Notificaciones">
  {#each toast.items as item (item.id)}
    <div
      class="toast"
      data-variant={item.variant}
      role={item.variant === 'error' ? 'alert' : 'status'}
      aria-live={item.variant === 'error' ? 'assertive' : 'polite'}
      onmouseenter={() => toast.pause(item.id)}
      onmouseleave={() => toast.resume(item.id)}
    >
      <span class="icon" aria-hidden="true">
        {#if item.variant === 'loading'}
          <Spinner size="sm" />
        {:else}
          <Icon name={iconFor(item.variant)} size={20} />
        {/if}
      </span>
      <p class="message">{item.message}</p>
      {#if item.action}
        <button
          type="button"
          class="action"
          onclick={() => {
            item.action?.onclick();
            toast.dismiss(item.id);
          }}
        >
          {item.action.label}
        </button>
      {/if}
      {#if item.dismissible}
        <button
          type="button"
          class="dismiss"
          aria-label="Cerrar notificación"
          onclick={() => toast.dismiss(item.id)}
        >
          <Icon name="x" size={14} />
        </button>
      {/if}
      
      {#if item.duration > 0 && item.variant !== 'loading'}
        <div 
          class="progress-bar" 
          class:paused={item.isPaused}
          style:--duration="{item.duration}ms"
        ></div>
      {/if}
    </div>
  {/each}
</div>

<style>
  .container {
    position: fixed;
    bottom: var(--space-4);
    right: var(--space-4);
    z-index: var(--z-toast);
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    pointer-events: none;
    max-width: calc(100vw - var(--space-8));
  }

  @media (max-width: 640px) {
    .container {
      left: var(--space-4);
      right: var(--space-4);
      max-width: none;
    }
  }

  .toast {
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    gap: var(--space-3);
    width: 320px;
    max-width: 100%;
    padding: var(--space-3) var(--space-4);
    background: var(--color-surface-elevated);
    border: var(--border-width) solid var(--color-border);
    border-left: 3px solid var(--color-info);
    border-radius: var(--radius-md);
    box-shadow: var(--shadow-md);
    pointer-events: auto;
    animation: toast-in var(--dur-base) var(--ease-out);
  }

  .toast[data-variant='success'] { border-left-color: var(--color-success); }
  .toast[data-variant='error']   { border-left-color: var(--color-danger); }
  .toast[data-variant='info']    { border-left-color: var(--color-info); }
  .toast[data-variant='loading'] { border-left-color: var(--color-accent); }

  .icon {
    display: inline-flex;
    align-items: center;
    flex: 0 0 auto;
  }
  .toast[data-variant='success'] .icon { color: var(--color-success); }
  .toast[data-variant='error'] .icon   { color: var(--color-danger); }
  .toast[data-variant='info'] .icon    { color: var(--color-info); }
  .toast[data-variant='loading'] .icon { color: var(--color-accent); }

  .message {
    margin: 0;
    flex: 1;
    min-width: 0;
    font-size: var(--text-sm);
    line-height: var(--text-sm-lh);
    color: var(--color-text-primary);
    overflow-wrap: anywhere;
  }

  .action {
    flex: 0 0 auto;
    padding: var(--space-1) var(--space-2);
    font-family: inherit;
    font-size: var(--text-caption);
    font-weight: var(--weight-medium);
    background: transparent;
    border: var(--border-width) solid var(--color-border-strong);
    border-radius: var(--radius-sm);
    color: var(--color-text-primary);
    cursor: pointer;
  }

  .action:hover {
    background: var(--color-surface-hover);
  }

  .dismiss {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    padding: 0;
    background: transparent;
    border: none;
    border-radius: var(--radius-sm);
    color: var(--color-text-muted);
    cursor: pointer;
  }

  .dismiss:hover {
    color: var(--color-text-primary);
  }

  .action:focus-visible,
  .dismiss:focus-visible {
    outline: var(--focus-ring-width) solid var(--color-focus-ring);
    outline-offset: 2px;
  }

  @keyframes toast-in {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .progress-bar {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: var(--color-text-muted);
    border-bottom-left-radius: var(--radius-md);
    border-bottom-right-radius: var(--radius-md);
    width: 100%;
    transform-origin: left;
    animation: progress-shrink var(--duration) linear forwards;
    opacity: 0.6;
  }

  .toast[data-variant='success'] .progress-bar { background: var(--color-success); }
  .toast[data-variant='error'] .progress-bar   { background: var(--color-danger); }
  .toast[data-variant='info'] .progress-bar    { background: var(--color-info); }

  .progress-bar.paused {
    animation-play-state: paused;
  }

  @keyframes progress-shrink {
    from { transform: scaleX(1); }
    to   { transform: scaleX(0); }
  }
</style>